import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ShieldCheck, Users, BookOpen, Briefcase, ArrowRight } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1601467995997-ac1ae9a8fff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxib3glMjB0cnVjayUyMGZsZWV0JTIwcGFya2VkJTIwd2FyZWhvdXNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3OTQwNzk1NXww&ixlib=rb-4.1.0&q=85';
const FLEET_IMG = 'https://images.unsplash.com/photo-1530226438127-d6a61eb82ac8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxib3glMjB0cnVjayUyMGZsZWV0JTIwcGFya2VkJTIwd2FyZWhvdXNlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3OTQwNzk1NXww&ixlib=rb-4.1.0&q=85';

const PILLARS = [
  { icon: ShieldCheck, title: 'Verified Networking', desc: 'Every member is identity-verified, building a trusted community free from fraud.' },
  { icon: BookOpen, title: 'Education & Training', desc: 'Access courses, guides, and certifications tailored for box truck operators.' },
  { icon: Users, title: 'Industry Connections', desc: 'Connect drivers, carriers, and dispatchers with meaningful business relationships.' },
  { icon: Briefcase, title: 'Business Opportunities', desc: 'Find loads, contracts, and partnerships that help small fleets grow.' },
];

const About = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="about-page">
    <Navbar />

    {/* Hero */}
    <section className="relative h-[420px] overflow-hidden">
      <img src={HERO_IMG} alt="Box truck fleet" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/80" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 text-center">
        <motion.img
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          src="https://customer-assets.emergentagent.com/job_verifiedlogistics/artifacts/n9slup3y_logo.JPG"
          alt="StraightConnect" className="h-14 w-auto mb-5"
        />
        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-3"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          About StraightConnect
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-slate-300 text-base max-w-xl"
        >
          Built for the box truck industry. Powered by community.
        </motion.p>
      </div>
    </section>

    {/* Mission Statement */}
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex flex-col lg:flex-row items-center gap-10">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex-1"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-5 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Our Mission
          </h2>
          <p className="text-slate-700 leading-relaxed text-base">
            StraightConnect is a growing platform built specifically for the box truck industry to connect drivers, carriers, dispatchers, and industry professionals through verified networking, education, resources, and business opportunities. The platform focuses on creating transparency, reducing fraud, and helping small transportation businesses access support, training, and meaningful industry connections.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="lg:w-[340px] flex-shrink-0"
        >
          <img src={FLEET_IMG} alt="Fleet at warehouse" className="w-full h-56 object-cover rounded-2xl shadow-lg" />
        </motion.div>
      </div>
    </section>

    {/* Pillars */}
    <section className="bg-slate-50 py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
          What We Stand For
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
                <p.icon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{p.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 text-center px-4">
      <h2 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Ready to join the network?
      </h2>
      <p className="text-slate-600 text-sm mb-6 max-w-md mx-auto">
        Whether you're a driver, carrier, or dispatcher — StraightConnect is your platform.
      </p>
      <Link
        to="/join"
        className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
        data-testid="about-join-btn"
      >
        Get Started <ArrowRight className="h-4 w-4" />
      </Link>
    </section>

    <Footer />
  </div>
);

export default About;
